import React, { useEffect, useState, useCallback } from "react";
import api, { describeError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { Download, Lock } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "cases", label: "Cases" },
  { key: "hearings", label: "Hearings" },
  { key: "clients", label: "Clients", premium: true },
  { key: "tasks", label: "Tasks", premium: true },
  { key: "courts", label: "Courts", premium: true },
  { key: "team", label: "Team", adminOnly: true, premium: true },
];

export default function Reports() {
  const { user, hasFeature } = useAuth();
  const [report, setReport] = useState("cases");
  const [data, setData] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const canFull = hasFeature("full_reports");
  const canExport = hasFeature("csv_export");
  const tabs = REPORTS.filter((r) => (!r.adminOnly || user?.role === "firm_admin") && (!r.premium || canFull));

  const load = useCallback(async () => {
    setData(null);
    try {
      const params = {};
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const { data } = await api.get(`/reports/${report}`, { params });
      setData(data);
    } catch (err) {
      toast.error(describeError(err, "Could not load this report."));
      setData({ summary: {}, rows: [], columns: [] });
    }
  }, [report, from, to]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    if (!canExport) { toast.error("CSV export is available on a higher plan. Upgrade to continue."); return; }
    try {
      const params = {}; if (from) params.date_from = from; if (to) params.date_to = to;
      const res = await api.get(`/reports/${report}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${report}_report.csv`;
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) { toast.error(describeError(err, "Export failed.")); }
  };

  const fmt = (v) => (v && typeof v === "object" ? JSON.stringify(v) : v ?? "—");

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" subtitle="Insights across your practice."
        actions={<Button onClick={exportCsv} data-testid="report-export-btn" className="bg-gold text-navy hover:bg-gold-dark">{canExport ? <Download className="mr-1.5 h-4 w-4" /> : <Lock className="mr-1.5 h-4 w-4" />}Export CSV</Button>} />

      {!canFull && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-gold/40 bg-gold/10 px-4 py-2.5 text-sm text-navy" data-testid="reports-upgrade-banner">
          <Lock className="h-4 w-4 text-gold-dark" />
          Detailed reports (Clients, Tasks, Courts, Team) and CSV export are available on Pro and Law Firm plans.
        </div>
      )}

      <Tabs value={report} onValueChange={setReport}>
        <TabsList className="mb-4 flex flex-wrap justify-start gap-1 bg-secondary/60">
          {tabs.map((r) => <TabsTrigger key={r.key} value={r.key} data-testid={`report-tab-${r.key}`}>{r.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {(report === "cases" || report === "hearings") && (
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" className="w-40" /></div>
          <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" className="w-40" /></div>
          {(from || to) && <Button variant="outline" onClick={() => { setFrom(""); setTo(""); }}>Clear</Button>}
        </div>
      )}

      {!data ? <LoadingState /> : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {Object.entries(data.summary).filter(([, v]) => typeof v !== "object").map(([k, v]) => (
              <Card key={k} className="border-border p-4 shadow-sm" data-testid={`report-stat-${k}`}>
                <p className="font-display text-3xl text-navy">{v}</p>
                <p className="mt-1 text-xs capitalize text-muted-foreground">{k.replace(/_/g, " ")}</p>
              </Card>
            ))}
          </div>
          <Card className="overflow-hidden border-border shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  {data.columns.map((c) => <th key={c} className="px-4 py-3 font-medium">{c.replace(/_/g, " ")}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-border">
                  {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="px-4 py-8 text-center text-muted-foreground">No data.</td></tr>}
                  {data.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-secondary/40">
                      {data.columns.map((c) => <td key={c} className="px-4 py-2.5 capitalize text-navy">{fmt(r[c])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
